import { getObjectTag } from '../internal-utils';

/**
 * Checks whether a value is an array.
 *
 * Uses `Array.isArray()` internally.
 *
 * @param value - The value to check.
 * @returns `true` if the value is an array.
 *
 * @example
 * isArray([]);            // true
 * isArray([1, 2, 3]);     // true
 * isArray('hello');       // false
 * isArray({ length: 1 }); // false
 */
export function isArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/**
 * Checks whether a value is a function.
 *
 * Accepts any callable: regular functions, arrow functions, async functions, generators, etc.
 *
 * @param value - The value to check.
 * @returns `true` if the value is a function.
 *
 * @example
 * isFunction(() => {});       // true
 * isFunction(function () {}); // true
 * isFunction(async () => {}); // true
 * isFunction(123);            // false
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export function isFunction(value: unknown): value is Function {
  return typeof value === 'function';
}

/**
 * Checks whether a value is a plain object.
 *
 * A plain object:
 * - Has the internal tag `[object Object]` (safe across realms/frames)
 * - Has a prototype of `Object.prototype` or `null`
 *
 * Rejects:
 * - `null`
 * - arrays
 * - functions
 * - class instances
 *
 * @param value - The value to check.
 * @returns `true` if the value is a plain object.
 *
 * @example
 * isPlainObject({});                  // true
 * isPlainObject(Object.create(null)); // true
 * isPlainObject([]);                  // false
 * isPlainObject(new MyClass());       // false
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    getObjectTag(value) === '[object Object]' &&
    (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
  );
}

/**
 * Checks whether a value is a valid `Date` object.
 *
 * This function supports cross-realm checks (e.g., when running inside an iframe).
 * It uses `Object.prototype.toString` to detect Date objects across global contexts.
 *
 * Accepts:
 * - instances of `Date` (even from other execution contexts like iframes)
 *
 * Rejects:
 * - invalid dates (`new Date('invalid')`, `new Date(NaN)`)
 * - non-Date objects
 *
 * @param value - The value to check.
 * @returns `true` if the value is a valid Date object.
 *
 * @example
 * isDate(new Date());               // true
 * isDate(new Date('2020-01-01'));   // true
 * isDate(new Date('invalid date')); // false
 * isDate(new Date(NaN));            // false
 * isDate('2020-01-01');             // false
 */
export function isDate(value: unknown): value is Date {
  return (
    typeof value === 'object' &&
    value !== null &&
    getObjectTag(value) === '[object Date]' &&
    !Number.isNaN((value as Date).getTime())
  );
}

/**
 * Checks whether a value is a `RegExp` object.
 *
 * Safe across realms (e.g., values from host frames or other contexts).
 *
 * @param value - The value to check.
 * @returns `true` if the value is a RegExp.
 *
 * @example
 * isRegExp(/abc/);               // true
 * isRegExp(new RegExp('abc'));   // true
 * isRegExp('abc');               // false
 */
export function isRegExp(value: unknown): value is RegExp {
  return getObjectTag(value) === '[object RegExp]';
}

/**
 * Checks whether a value is a `Map` instance.
 *
 * Safe across realms (e.g., from host frame or another execution context).
 *
 * @param value - The value to check.
 * @returns `true` if the value is a Map.
 *
 * @example
 * isMap(new Map());     // true
 * isMap(new WeakMap()); // false
 * isMap({});            // false
 */
export function isMap(value: unknown): value is Map<unknown, unknown> {
  return getObjectTag(value) === '[object Map]';
}

/**
 * Checks whether a value is a `Set` instance.
 *
 * Safe across realms (e.g., from host frame or another execution context).
 *
 * @param value - The value to check.
 * @returns `true` if the value is a Set.
 *
 * @example
 * isSet(new Set());     // true
 * isSet(new WeakSet()); // false
 * isSet([]);            // false
 */
export function isSet(value: unknown): value is Set<unknown> {
  return getObjectTag(value) === '[object Set]';
}

/**
 * Checks whether a value is a native `Promise` instance.
 *
 * This does **not** include general thenables — only real Promises.
 * Safe across realms (iframe/host) using `Object.prototype.toString`.
 *
 * @param value - The value to check.
 * @returns `true` if the value is a native Promise.
 *
 * @example
 * isPromise(Promise.resolve());     // true
 * isPromise(new Promise(() => {})); // true
 * isPromise({ then() {} });         // false
 */
export function isPromise<T = unknown>(value: unknown): value is Promise<T> {
  return typeof value === 'object' && value !== null && getObjectTag(value) === '[object Promise]';
}
